import { useEffect, useCallback } from 'react';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signOut,
  updateProfile,
  onAuthStateChanged,
  type User,
} from 'firebase/auth';
import { auth, googleProvider } from '@/lib/firebase';
import { useAuthStore } from '@/store/authStore';
import {
  upsertUserProfile,
  createLoginSession,
  endLoginSession,
} from '@/services/activityLogger';
import { fetchProfileStatus } from '@/services/invitationService';

export function useAuth() {
  const {
    user,
    loading,
    authError,
    currentSessionId,
    isApprovedHost,
    isAdmin,
    setUser,
    setLoading,
    setAuthError,
    setCurrentSessionId,
    setIsApprovedHost,
    setIsAdmin,
    clearAuth,
  } = useAuthStore();

  // ── Helper to fetch and apply profile status ──────────────
  const refreshProfileStatus = useCallback(async () => {
    try {
      const profile = await fetchProfileStatus();
      if (profile) {
        setIsApprovedHost(profile.isApprovedHost);
        setIsAdmin(profile.isAdmin);
        return profile;
      }
    } catch (e) {
      console.warn('[useAuth] Error fetching profile status:', e);
    }

    // Default to unprivileged state if backend is unreachable or status could not be verified
    setIsApprovedHost(false);
    setIsAdmin(false);
    return null;
  }, [setIsApprovedHost, setIsAdmin]);

  // ── Restore Persistent Auth Session on Mount ────────────────
  useEffect(() => {
    // Listen to Firebase persistent session state changes
    const unsubscribe = onAuthStateChanged(
      auth,
      async (firebaseUser: User | null) => {
        if (firebaseUser) {
          setUser(firebaseUser);

          // Update user profile record in Firestore (silent fail if offline)
          try {
            await upsertUserProfile(firebaseUser.uid, {
              email: firebaseUser.email,
              displayName: firebaseUser.displayName,
            });
          } catch { }

          // Fetch host approval and admin status strictly from backend
          await refreshProfileStatus();

          // Ensure an active session ID is tracked for this device
          try {
            const activeSessionId = useAuthStore.getState().currentSessionId;
            if (!activeSessionId) {
              const authProvider = firebaseUser.providerData[0]?.providerId || 'password';
              const sessionId = await createLoginSession(firebaseUser.uid, authProvider);
              if (sessionId) {
                setCurrentSessionId(sessionId);
              }
            }
          } catch { }
        } else {
          clearAuth();
        }
      },
      (error) => {
        console.warn('[useAuth] onAuthStateChanged error:', error);
        setAuthError(error.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [setUser, setLoading, setAuthError, setCurrentSessionId, setIsApprovedHost, setIsAdmin, clearAuth, refreshProfileStatus]);

  // ── Helper: Format Firebase Auth Errors ──────────────────────
  const formatAuthError = (err: unknown): string => {
    const code = (err as { code?: string })?.code || '';
    switch (code) {
      case 'auth/invalid-credential':
      case 'auth/wrong-password':
      case 'auth/user-not-found':
        return 'Incorrect email or password. Please verify and try again.';
      case 'auth/email-already-in-use':
        return 'An account already exists with this email address. Please sign in instead.';
      case 'auth/weak-password':
        return 'Password must be at least 6 characters long.';
      case 'auth/invalid-email':
        return 'Please enter a valid email address.';
      case 'auth/popup-closed-by-user':
        return 'Sign-in window was closed. Please try again.';
      case 'auth/popup-blocked':
        return 'Pop-up was blocked by your browser. Please allow pop-ups for this site.';
      case 'auth/network-request-failed':
        return 'Network connection error. Please check your internet connection.';
      default:
        return (err as Error)?.message || 'Authentication failed. Please try again.';
    }
  };

  // ── Email & Password Sign In ────────────────────────────────
  const loginWithEmail = useCallback(
    async (email: string, pass: string): Promise<User | null> => {
      setAuthError(null);
      setLoading(true);
      try {
        const cred = await signInWithEmailAndPassword(auth, email.trim(), pass);
        const loggedInUser = cred.user;
        setUser(loggedInUser);

        await upsertUserProfile(loggedInUser.uid, {
          email: loggedInUser.email,
          displayName: loggedInUser.displayName,
        });

        const sessionId = await createLoginSession(loggedInUser.uid, 'password');
        if (sessionId) setCurrentSessionId(sessionId);

        return loggedInUser;
      } catch (err) {
        const message = formatAuthError(err);
        setAuthError(message);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [setAuthError, setLoading, setUser, setCurrentSessionId]
  );

  // ── Email & Password Registration ───────────────────────────
  const registerWithEmail = useCallback(
    async (email: string, pass: string, displayName?: string): Promise<User | null> => {
      setAuthError(null);
      setLoading(true);
      try {
        const cred = await createUserWithEmailAndPassword(auth, email.trim(), pass);
        const newUser = cred.user;

        if (displayName?.trim()) {
          await updateProfile(newUser, { displayName: displayName.trim() });
        }

        setUser(newUser);

        await upsertUserProfile(newUser.uid, {
          email: newUser.email,
          displayName: displayName?.trim() || newUser.displayName,
        });

        const sessionId = await createLoginSession(newUser.uid, 'password');
        if (sessionId) setCurrentSessionId(sessionId);

        return newUser;
      } catch (err) {
        const message = formatAuthError(err);
        setAuthError(message);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [setAuthError, setLoading, setUser, setCurrentSessionId]
  );

  // ── Google Authentication ───────────────────────────────────
  const loginWithGoogle = useCallback(async (): Promise<User | null> => {
    setAuthError(null);
    setLoading(true);
    try {
      const cred = await signInWithPopup(auth, googleProvider);
      const googleUser = cred.user;
      setUser(googleUser);

      await upsertUserProfile(googleUser.uid, {
        email: googleUser.email,
        displayName: googleUser.displayName,
      });

      const sessionId = await createLoginSession(googleUser.uid, 'google');
      if (sessionId) setCurrentSessionId(sessionId);

      return googleUser;
    } catch (err) {
      const message = formatAuthError(err);
      setAuthError(message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [setAuthError, setLoading, setUser, setCurrentSessionId]);

  // ── Explicit Manual Sign Out ────────────────────────────────
  const logout = useCallback(async () => {
    try {
      if (user && currentSessionId) {
        await endLoginSession(user.uid, currentSessionId);
      }
      await signOut(auth);
      clearAuth();
    } catch (err) {
      console.warn('[useAuth] signOut error:', err);
      clearAuth();
    }
  }, [user, currentSessionId, clearAuth]);

  return {
    user,
    loading,
    authError,
    isAuthenticated: !!user,
    isApprovedHost,
    isAdmin,
    loginWithEmail,
    registerWithEmail,
    loginWithGoogle,
    logout,
    refreshProfileStatus,
    clearAuthError: () => setAuthError(null),
  };
}

